import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { API, LEAD_STATUSES } from "@/lib/admin-constants";

const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, leadsRes] = await Promise.all([
          axios.get(`${API}/stats`, { withCredentials: true }),
          axios.get(`${API}/leads`, { withCredentials: true }),
        ]);
        setStats(statsRes.data);
        setRecentLeads(leadsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Dashboard fetch failed:", error);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { label: "Total Leads", value: stats?.total_leads || 0, color: "bg-cfc-primary" },
    { label: "New Leads", value: stats?.new_leads || 0, color: "bg-blue-500" },
    { label: "Contacted", value: stats?.contacted || 0, color: "bg-yellow-500" },
    { label: "Trial Scheduled", value: stats?.trial_scheduled || 0, color: "bg-purple-500" },
    { label: "Joined", value: stats?.joined || 0, color: "bg-green-500" },
  ];

  return (
    <div data-testid="dashboard-overview">
      <h1 className="font-heading text-3xl font-bold text-cfc-primary mb-8">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat) => (
          <Card key={stat.label} className="border-cfc-border" data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-4">
              <div className={`w-3 h-3 rounded-full ${stat.color} mb-2`} />
              <p className="text-2xl font-bold text-cfc-primary">{stat.value}</p>
              <p className="text-sm text-cfc-text-muted">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-cfc-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl text-cfc-primary">Recent Leads</CardTitle>
          <Link to="/admin/pipeline">
            <Button variant="ghost" size="sm" className="text-cfc-accent">
              View All <ChevronRight size={16} className="ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Age Group</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentLeads.map((lead) => (
                <TableRow key={lead.lead_id} data-testid={`lead-row-${lead.lead_id}`}>
                  <TableCell className="font-medium">
                    {lead.name}
                    {lead.lead_type === "coach" && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-[#0C2E3C] text-[#C8E64E]">
                        Coach/Team
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>{lead.player_age}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      LEAD_STATUSES.find((s) => s.id === lead.status)?.color || "bg-gray-500"
                    } text-white`}>
                      {LEAD_STATUSES.find((s) => s.id === lead.status)?.label || lead.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {recentLeads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-cfc-text-muted py-8">
                    No leads yet. They{"'"}ll appear here when someone registers interest.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardOverview;
